import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { useTranslation } from 'react-i18next';
import { Toolbar } from "../../components/ui/toolbar/toolbar.component";
import { Action } from "../../types/toolbar";
import CustomModal from "../../components/ui/modal/modal.component";
import { useState, useEffect } from "react";
import { EQUIPMENT_COUNTERS_ENDPOINT, EQUIPMENT_COUNTERS_VERIFY_ENDPOINT, SUCCESS_SNACKBAR, ERROR_SNACKBAR } from "../../consts";
import EquipmentCounterForm from "../../components/ui/equipmentCounterForm/equipmentCounterForm.component";
import { useParams, useNavigate } from "react-router-dom";
import { useCRUD } from "../../api/crud";
import { useApiRequest } from "../../api/axios";
import axios from "../../api/axios";
import { useSnackbar } from 'notistack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useHasPermission } from "../../contexts/auth";

export const EquipmentCounterDetailActions = () => {
  const { id, counterId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { loading, error, remove, get } = useCRUD(EQUIPMENT_COUNTERS_ENDPOINT);
  const { loading: verifying, executeRequest } = useApiRequest();
  const [updateCounterOpened, setUpdateCounterOpened] = useState<boolean>(false);
  const [deleteCounterOpened, setDeleteCounterOpened] = useState<boolean>(false);
  const [counterVerifiedAt, setCounterVerifiedAt] = useState<string | null | undefined>(undefined);
  const canChangeCounter = useHasPermission("settlement.change_equipmentcounter");
  const canDeleteCounter = useHasPermission("settlement.delete_equipmentcounter");

  useEffect(() => {
    const loadCounter = async () => {
      if (!counterId) return;
      const response = await get(counterId);
      if (response?.data?.verified_at != null) {
        setCounterVerifiedAt(response.data.verified_at);
      } else {
        setCounterVerifiedAt(null);
      }
    };
    loadCounter();
    const handleUpdated = () => loadCounter();
    window.addEventListener("equipmentCounterUpdated", handleUpdated);
    return () => window.removeEventListener("equipmentCounterUpdated", handleUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when counterId changes
  }, [counterId]);

  const handleDeleteCounter = async () => {
    if (loading || !counterId) {
      return;
    }

    const result = await remove({ id: counterId });

    if (result?.success) {
      enqueueSnackbar(t('Counter deleted successfully'), SUCCESS_SNACKBAR);
      setDeleteCounterOpened(false);
      navigate(`/equipment-section/equipments/${id}/counters`);
    } else {
      enqueueSnackbar(t('Failed to delete counter'), ERROR_SNACKBAR);
    }
  };

  const handleVerifyCounter = async () => {
    if (!counterId || counterVerifiedAt) return;
    const result = await executeRequest(() =>
      axios.post(EQUIPMENT_COUNTERS_VERIFY_ENDPOINT, {
        counter_ids: [counterId],
      })
    );
    if (result?.success) {
      enqueueSnackbar(t("Counter marked as verified"), SUCCESS_SNACKBAR);
      window.dispatchEvent(new Event("equipmentCounterUpdated"));
    } else {
      enqueueSnackbar(t("Failed to verify counter"), ERROR_SNACKBAR);
    }
  };

  const actions: Action[] = [
    {
      icon: <EditIcon />,
      label: t("Update counter"),
      onClick: () => {
        setUpdateCounterOpened(true);
      },
      disabled: !canChangeCounter,
    },
    {
      icon: <VerifiedUserIcon />,
      label: t("Verify counter"),
      onClick: handleVerifyCounter,
      disabled: !canChangeCounter || !!counterVerifiedAt || verifying,
    },
    {
      icon: <DeleteIcon />,
      label: t("Delete counter"),
      onClick: () => {
        setDeleteCounterOpened(true);
      },
      color: "var(--errorColor)",
      disabled: !canDeleteCounter,
    },
  ];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.message ?? error?.detail ?? '';

  const deleteModalContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {t('Deleting this counter will permanently remove it from the system. This action cannot be undone.')}
      </Typography>

      <Alert severity="warning">
        <Typography variant="body2">
          {t('This action cannot be undone. All associated data will be permanently deleted.')}
        </Typography>
      </Alert>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Button
        variant="contained"
        color="error"
        onClick={handleDeleteCounter}
        disabled={loading}
      >
        {loading ? t('Processing...') : t('Confirm deletion')}
      </Button>
    </Box>
  );

  return (
    <>
      <Toolbar actions={actions} />
      <CustomModal
        opened={updateCounterOpened}
        setOpened={setUpdateCounterOpened}
        title={t("Update counter")}
        content={
          <EquipmentCounterForm
            id={counterId}
            onSuccess={() => {
              setUpdateCounterOpened(false);
              window.dispatchEvent(new Event("equipmentCounterUpdated"));
            }}
            hiddenFields={["equipment_id", "kind", "job"]}
          />
        }
      />
      <CustomModal
        opened={deleteCounterOpened}
        setOpened={setDeleteCounterOpened}
        title={t("Delete counter")}
        content={deleteModalContent}
      />
    </>
  );
};
